import React, { useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLongArrowAltLeft } from '@fortawesome/free-solid-svg-icons';
import logo from '../../assets/images/logo.png';
import { AppContext } from '../../context/appContext';

const OrderSummary = ({
  resetState,
  company,
  orderId,
  productList,
  name,
  email,
  phone,
  address,
  location,
  date,
  time,
  payMethod,
  total,
  orderSum,
  deliveryFee,
  closeModal,
  transactionCode
}) => {
  const [settings] = useContext(AppContext);
  const goBack = () => {
    resetState();
    closeModal();
  };
  return (
    <Root>
      <Header>
        <img
          src={settings?.footerLogo ? `upload/${settings.footerLogo}` : logo}
          alt="logo"
        />
        <div className="title-wrapper">
          <h3>
            {company} {settings?.title || ' #Érintésmentes'}
          </h3>
        </div>
      </Header>
      <Title bgcolor={settings?.productListHeader || '#e7c37a'}>
        Sikeres rendelés!
      </Title>
      <p>
        Az ön által leadott rendelést {orderId} sorszámon rögzítettük,
        tartalmáról az ön által megadott email címre üzenetet küldtünk.
      </p>
      {transactionCode && (
        <p>SimplePay tranzakciós azonosító: {transactionCode}</p>
      )}
      <div>
        <ProductRow
          borderColor={settings?.productListHeader || '#e7c37a'}
          type="header"
        >
          <p>Termék neve</p>
          <p>Egységár</p>
          <p>Mennyiség</p>
        </ProductRow>
        {productList.map(row => (
          <ProductRow
            borderColor={settings?.productListHeader || '#e7c37a'}
            key={row.name}
          >
            <p>{row.name}</p>
            <p>{row.price}</p>
            <p>{row.value}</p>
          </ProductRow>
        ))}
        <ProductRow
          borderColor={settings?.productListHeader || '#e7c37a'}
          type="total"
        >
          <p>
            <strong>Termékek összesen:</strong>
          </p>
          <p>
            <strong>{orderSum}</strong>
          </p>
        </ProductRow>
      </div>
      <Summary bgcolor={settings?.productListHeader || '#e7c37a'}>
        <SummaryRow>
          <p>
            <strong>Megadott név:</strong>
          </p>
          <p>{name}</p>
        </SummaryRow>
        <SummaryRow>
          <p>
            <strong>Megadott email cím:</strong>
          </p>
          <p>{email}</p>
        </SummaryRow>
        <SummaryRow>
          <p>
            <strong>Megadott telefonszám:</strong>
          </p>
          <p>{phone}</p>
        </SummaryRow>
        <SummaryRow>
          <p>
            <strong>Megadott cím:</strong>
          </p>
          <p>
            {address.zip} {address.city} {address.street} {address.house}
          </p>
        </SummaryRow>
      </Summary>
      <Summary bgcolor={settings?.productListHeader || '#e7c37a'}>
        <SummaryRow>
          <p>
            <strong>Atvételi helyszín:</strong>
          </p>
          <p>{location}</p>
        </SummaryRow>
        <SummaryRow>
          <p>
            <strong>Atvételi dátum:</strong>
          </p>
          <p>{date}</p>
        </SummaryRow>
        <SummaryRow hide={time === 'today'}>
          <p>
            <strong>Átvételi idősáv:</strong>
          </p>
          <p>{time}</p>
        </SummaryRow>
        {deliveryFee && (
          <SummaryRow>
            <p>
              <strong>Szállítási költség:</strong>
            </p>
            <p>{deliveryFee}</p>
          </SummaryRow>
        )}
      </Summary>
      <Summary bgcolor={settings?.productListHeader || '#e7c37a'}>
        <SummaryRow>
          <p>
            <strong>Fizetési mód:</strong>
          </p>
          <p>{payMethod}</p>
        </SummaryRow>
      </Summary>
      <Summary bgcolor={settings?.productListHeader || '#e7c37a'}>
        <SummaryRow type="total">
          <p>
            <strong>Összesen fizetendő:</strong>
          </p>
          <p>{total}</p>
        </SummaryRow>
      </Summary>
      <Footer>
        <button onClick={goBack} type="button">
          <Icon icon={faLongArrowAltLeft} size="3x" /> Vissza egy újabb
          rendeléshez
        </button>
      </Footer>
    </Root>
  );
};

export default OrderSummary;

OrderSummary.propTypes = {
  resetState: PropTypes.func.isRequired,
  company: PropTypes.string,
  orderId: PropTypes.string,
  productList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      price: PropTypes.string,
      value: PropTypes.string
    })
  ),
  name: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  address: PropTypes.shape(),
  location: PropTypes.string,
  date: PropTypes.string,
  time: PropTypes.string,
  payMethod: PropTypes.string,
  total: PropTypes.string,
  orderSum: PropTypes.string,
  deliveryFee: PropTypes.string,
  closeModal: PropTypes.func.isRequired,
  transactionCode: PropTypes.string
};

OrderSummary.defaultProps = {
  company: 'Teszt Parola Cég Kft.',
  orderId: 'DDDDDDDD/LL/TT/CCC',
  productList: [
    {
      name: 'Termék 1 /kg',
      price: '990 Ft',
      value: '22.0 kg'
    },
    {
      name: 'Termék 2 /db',
      price: '350 Ft',
      value: '12 db'
    }
  ],
  name: 'Gipsz Jakab',
  email: 'gipszjakab@email.hu',
  phone: '+36301234567',
  address: {
    zip: '1111',
    city: 'Budapest',
    street: 'Fő utca',
    house: '1'
  },
  location: '1. Bolt - 1111. Budapest, Fő utca 1.',
  date: '2020.04.25',
  time: '08-10 óra',
  payMethod: 'Bankkártya (átvételkor)',
  total: null,
  orderSum: null,
  deliveryFee: null,
  transactionCode: null
};

const Root = styled.div`
  width: 100%;
  height: 100%;
  max-width: 800px;
  margin: 0 auto;
  max-height: 90vh;
`;

const Header = styled.div`
  display: flex;
  max-height: 100px;
  align-items: center;

  & img {
    height: 100px;
    width: auto;
  }

  & .title-wrapper {
    width: 90%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const Title = styled.h1`
  background-color: ${props => props.bgcolor};
  padding: 8px 0;
  text-align: center;
`;

const ProductRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  background-color: ${props => (props.type === 'header' ? 'black' : 'inherit')};
  color: ${props => (props.type === 'header' ? 'white' : 'black')};
  font-size: ${props => (props.type === 'total' ? 'larger' : '')};
  border-bottom: ${props => `2px solid ${props.borderColor}`};

  & p {
    margin: 2px 4px;
    &:not(:nth-child(1)) {
      text-align: right;
      width: 80px;
    }
  }

  & :nth-child(1) {
    width: 60%;
  }
`;

const Summary = styled.div`
  background-color: ${props => props.bgcolor};
  padding: 8px 4px;
`;

const SummaryRow = styled.div`
  display: ${props => (props.hide === true ? 'none' : 'flex')};
  font-size: ${props => (props.type === 'total' ? '20px' : 'inherit')};
  font-weight: ${props => (props.type === 'total' ? '700' : '400')};

  & :nth-child(1) {
    width: 30%;
    min-width: 150px;
  }

  & * {
    margin: 4px 0;
  }
`;

const Footer = styled.div`
  background-color: black;
  width: 100%;

  & button {
    background: none;
    border: none;
    color: white;
    display: flex;
    align-items: center;
    font-size: 16px;
    cursor: pointer;
    border: 2px solid white;
  }
`;

const Icon = styled(FontAwesomeIcon)`
  margin-right: 8px;
`;
